import { Grid, IconButton, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';

import React, { useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import './Orders.scss';
import { Delete, Edit, Update } from '@mui/icons-material';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import Typography from '@mui/material/Typography';
import TimeUtils from '../common/TimeUtils';
import './UpcomingOrders.scss';
import { useCalendar } from '../calendar/CalendarContext/CalendarContext';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { AccountData, OrderData, OrderPageData, OrderService, OrderStatus } from '../api';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';
import { useAuth } from '../session/InternalAuthProvider';
import { useLanguage } from '../common/GeneralUtils';
import ConfirmationDialog from '../common/ui/ConfimationDialog';
import EditOrderDialog from '../calendar/dialogs/EditOrderDialog/EditOrderDialog';
import { CellProps, useTable } from '../table/useTable';
import { getDateFormat, getTimeFormat } from '../common/getDateAndTimeFormat';
import { CalendarView, DATE_FORMAT } from '../calendar/calendarWrapper/CalendarWrapper';
import { TABLE_PHONE_ID, TABLE_PRICE_ID } from '../table/TableComponent';
import { getOrderPages } from '../common/AccountUtils';

type OrdersTableProps = {
  fetchingMethod: (page?: number, perPage?: number, search?: string, statuses?: OrderStatus[]) => Promise<OrderData[]>;
  opId: string;
  hideActions?: boolean;
  withArchivedColumns?: boolean;
};
export const OrdersTable = ({ fetchingMethod, opId, hideActions, withArchivedColumns }: OrdersTableProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;

  const language = useLanguage();
  const [selectedOrder, setSelectedOrder] = useState<OrderData | undefined>();
  const [deleteOrderIsOpen, setDeleteOrderIsOpen] = useState(false);
  const { readOnly, isAtLeastSupervisor } = UseAuthPermissions();
  const timeFormat = getTimeFormat(account);

  const { shortDateFormat, longDateFormat } = getDateFormat(account);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fetcher = async ({ search, page, perPage }: { search?: string; page: number; perPage: number }) =>
    fetchingMethod(page, perPage, search);

  const { openEditOrderDrawer, editOrderDrawerState, closeEditOrderDrawer, users } = useCalendar();
  const selfServeEditsEnabled = useFeatureFlagEnabled('self-serve-edits');

  function formatCreatedAt(created_at: string) {
    const createdAt = TimeUtils.getLocalTime(created_at, account.timezone);
    const isToday = createdAt.isSame(moment(), 'day');
    return (
      <Typography
        variant={isToday ? 'body1' : 'body2'}
        color={isToday ? '#4abd00' : undefined}
        fontWeight={isToday ? 700 : undefined}>
        {createdAt.format(isToday ? timeFormat : shortDateFormat)}
      </Typography>
    );
  }

  const archivedColumns: CellProps<OrderData>[] = [
    {
      id: 'archived_by',
      alignment: 'center',
      label: t('calendar.edit_order.archived_by'),
      hideCell: !withArchivedColumns,
      render: (order: OrderData) => {
        const archivedByUser = users?.find((u) => u.user_id === order.archived_by_user_id);
        return archivedByUser ? `${archivedByUser.first_name} ${archivedByUser.last_name}` : order.archived_by_user_id;
      },
    },
    {
      id: 'archive_timestamp',
      alignment: 'center',
      label: t('calendar.edit_order.archived_at'),
      hideCell: !withArchivedColumns,
      render: (order: OrderData) =>
        order.archive_timestamp && moment(order.archive_timestamp).format(`${longDateFormat}`),
    },
  ];
  const tableDetails: CellProps<OrderData>[] = [
    {
      id: 'name',
      alignment: 'center',
      label: t('orders.name'),
      render: (order: OrderData) => `${order.firstname} ${order.lastname}`,
    },
    {
      id: 'createdAt',
      alignment: 'center',
      label: t('orders.date'),
      render: (order: OrderData) => formatCreatedAt(order.created_at),
    },
    {
      id: 'numPersons',
      alignment: 'center',
      label: t('orders.num_persons'),
      render: (order: OrderData) => order.persons.toString(),
    },
    {
      id: 'orderDate',
      alignment: 'center',
      label: t('order_date'),
      render: (order: OrderData) => {
        const momentDate = moment(order.date);
        const calendarView = localStorage.getItem('webook-calendarview');
        // @ts-ignore
        const weekStart = momentDate.clone().startOf(calendarView === CalendarView.THREE_DAY ? 'day' : calendarView);
        return (
          <Link to={`/dashboard/calendar/${weekStart.format(DATE_FORMAT)}?entityid=${order.parent_id || order.id}`}>
            {momentDate.format(shortDateFormat)}
          </Link>
        );
      },
    },
    {
      id: TABLE_PHONE_ID,
      alignment: 'center',
      width: 140,
      label: t('orders.phone'),
    },
    {
      id: 'email',
      alignment: 'center',
      label: t('orders.email'),
      render: (order: OrderData) => <Typography style={{ textTransform: 'none' }}>{order.email}</Typography>,
    },
    {
      id: 'workshop',
      alignment: 'center',
      label: t('orders.workshop'),
      render: (order: OrderData) => {
        const orderPage = getOrderPages(account).find((x: OrderPageData) => x.id === order.order_page_id);
        return (
          <Grid container gap={1} flexWrap="nowrap" alignItems={'center'}>
            <WorkshopCircle account={account} orderPageId={order.order_page_id} language={language} withTooltip />
            <Typography
              variant={'body2'}
              sx={{
                maxWidth: 100,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {orderPage?.label[language]}
            </Typography>
          </Grid>
        );
      },
    },
    {
      id: TABLE_PRICE_ID,
      alignment: 'center',
      label: t('orders.price'),
      width: 100,
    },
    {
      id: 'orderNumber',
      alignment: 'center',
      label: t('order_number'),
      render: (order: OrderData) => `#${order.order_number}`,
    },
    {
      id: 'duration',
      alignment: 'center',
      label: t('orders.duration'),
      render: (order: OrderData) => TimeUtils.formatDuration(order.duration, t),
    },
    {
      id: 'status',
      alignment: 'center',
      label: t('orders.status'),
      render: (order: OrderData) => t(`order_status.${order.status.toLowerCase()}`),
    },
    {
      id: 'update',
      alignment: 'left',
      hideCell: hideActions,
      width: 30,
      noPadding: true,
      render: (order: OrderData) => (
        <a
          href={
            !readOnly && selfServeEditsEnabled && order.status !== OrderStatus.CONVERTED_TO_VOUCHER
              ? `${window.location.origin}/manage-booking?orderid=${order.id}`
              : undefined
          }
          target="_blank"
          rel="noopener noreferrer">
          <Tooltip
            title={
              !isAtLeastSupervisor ? t('noPermissions') : selfServeEditsEnabled ? t('amend') : t('amend_not_enabled')
            }
            arrow>
            <span>
              <IconButton disabled={!isAtLeastSupervisor || order.status === OrderStatus.CONVERTED_TO_VOUCHER}>
                <Update />
              </IconButton>
            </span>
          </Tooltip>
        </a>
      ),
    },
  ];

  if (withArchivedColumns) {
    tableDetails.push(...archivedColumns);
  }
  tableDetails.push({
    id: 'delete',
    alignment: 'left',
    hideCell: hideActions,
    width: 30,
    noPadding: true,
    render: (order: OrderData) => (
      <Tooltip title={readOnly ? t('noPermissions') : t('delete')}>
        <span>
          <IconButton
            disabled={readOnly || order.status === OrderStatus.CONVERTED_TO_VOUCHER}
            className="action-icon"
            onClick={() => {
              setSelectedOrder(order);
              setDeleteOrderIsOpen(true);
            }}>
            <Delete />
          </IconButton>
        </span>
      </Tooltip>
    ),
  });
  tableDetails.push({
    id: 'edit',
    alignment: 'left',
    width: 30,
    noPadding: true,
    render: (order: OrderData) => (
      <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('edit')}>
        <span>
          <IconButton
            disabled={!isAtLeastSupervisor || order.status === OrderStatus.CONVERTED_TO_VOUCHER}
            className="action-icon"
            onClick={() => {
              openEditOrderDrawer({ order, orderPageId: order.order_page_id, parentId: order.parent_id });
            }}>
            <Edit />
          </IconButton>
        </span>
      </Tooltip>
    ),
  });
  const { mutate, data, TableComponent } = useTable<OrderData>({
    opId,
    fetcher,
    tableDetails,
  });
  return (
    <Grid container>
      {TableComponent}
      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={closeEditOrderDrawer}
        updateOrder={(orderToUpdate: OrderData) => {
          const orders = data?.filter((x) => x.id !== selectedOrder?.id) || [];
          void mutate([...orders, orderToUpdate]);
        }}
      />
      <ConfirmationDialog
        title={t('calendar.delete_order.title')}
        content={<Typography>{t('calendar.delete_order.description')}</Typography>}
        isOpen={deleteOrderIsOpen}
        onClose={async (actionConfirmed) => {
          if (actionConfirmed && selectedOrder) {
            await OrderService.deleteOrder(selectedOrder.id);
            const orders = data?.filter((x) => x.id !== selectedOrder.id) || [];
            void mutate(orders);
          }
          setDeleteOrderIsOpen(false);
        }}
      />
    </Grid>
  );
};
